/**
 * Platform notices (architecture §11; ui-ux §6.2, §6.26, §3.4): the one-time lite banner «Без миниатюр» and its details
 * plate with «Попробовать снова», the E6 device-lost stage notice, the read-only banner «Список новее писца», the
 * second-tab notice and the E0 fatal plate. Plain DOM in the illuminated style (`styles.ts`); F3's kit may restyle.
 */
import type { TFunction } from '../shared';
import { button as kitButton } from '../ui';
import type { KeyValueStorage } from './save';

/** Why the session runs «Без миниатюр». */
export type LiteReason = 'no-api' | 'insecure' | 'no-adapter' | 'no-device' | 'pipeline' | 'lost' | 'manual';

/** ui-ux §6.2: ✕ hides the banner for this device. */
export const LITE_BANNER_KEY = 'marg.lite.bannerSeen';
let liteResize: ResizeObserver | null = null;

const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
};
/** F3's kit button (primary = vermilion, plain = secondary), full width inside the notice row. */
const button = (label: string, plain: boolean, onClick: () => void): HTMLButtonElement =>
  kitButton({ label, variant: plain ? 'secondary' : 'primary', size: 'block', onClick: () => onClick() }) as HTMLButtonElement;

function bannerKey(reason: LiteReason): string {
  switch (reason) {
    case 'no-api': return 'app.lite.banner.noApi';
    case 'insecure': return 'app.lite.banner.insecure';
    case 'lost': return 'app.lite.banner.lost';
    default: return 'app.lite.banner.noAdapter';
  }
}

export interface LiteBannerOpts {
  reason: LiteReason;
  /** Error code for E4 («Подробнее» shows it in 15 px inkFaded). */
  code?: string;
  t: TFunction;
  storage: KeyValueStorage | null;
  /** Re-runs the GPU step; resolves true on success. */
  retry(): Promise<boolean>;
  /** Show even if dismissed before (`?nowebgpu=1` tests). */
  force?: boolean;
}

/** Shows the one-time lite banner (E1–E4); returns null when the player dismissed it on this device before. */
export function showLiteBanner(host: HTMLElement, o: LiteBannerOpts): HTMLElement | null {
  if (!o.force && o.storage?.getItem(LITE_BANNER_KEY) === '1') return null;
  host.querySelector('.mc-banner[data-banner="lite"]')?.remove();
  liteResize?.disconnect();
  const bar = el('div', 'mc-banner');
  bar.dataset.banner = 'lite';
  bar.setAttribute('role', 'status');
  const text = el('div', 'mc-banner__text');
  const title = el('b', undefined, `${o.t('app.lite.name')}.`);
  text.append(title, document.createTextNode(` ${o.t(bannerKey(o.reason))} `));
  const more = el('button', 'mc-app-link', o.t('app.lite.more'));
  more.type = 'button';
  more.addEventListener('click', () => showLiteDetails(o));
  text.appendChild(more);
  const close = el('button', 'mc-banner__close', '✕');
  close.type = 'button';
  close.setAttribute('aria-label', o.t('app.lite.close'));
  close.addEventListener('click', () => {
    try { o.storage?.setItem(LITE_BANNER_KEY, '1'); } catch { /* ignore */ }
    bar.remove();
    liteResize?.disconnect(); liteResize = null;
    document.documentElement.style.removeProperty('--notice-top');
  });
  bar.append(text, close);
  host.appendChild(bar);
  // Keep the notice outside the playable page, including narrow phone layouts.
  const reserve = (): void => document.documentElement.style.setProperty('--notice-top', `${bar.getBoundingClientRect().height + 18}px`);
  liteResize = new ResizeObserver(reserve); liteResize.observe(bar); reserve();
  return bar;
}

/** «Подробнее ›»: what lite mode is, where miniatures work, and «Попробовать снова». */
export function showLiteDetails(o: LiteBannerOpts): HTMLDialogElement {
  const dlg = el('dialog', 'mc-notice-dialog');
  dlg.setAttribute('aria-labelledby', 'mc-lite-title');
  const plate = el('div', 'mc-notice-plate');
  const h = el('h2', undefined, o.t('app.lite.name'));
  h.id = 'mc-lite-title';
  const list = el('ul');
  for (const k of ['app.lite.sheet.b1', 'app.lite.sheet.b2', 'app.lite.sheet.b3', 'app.lite.sheet.b4']) list.appendChild(el('li', undefined, o.t(k)));
  const note = el('div', 'mc-notice-plate__note');
  note.setAttribute('aria-live', 'polite');
  const row = el('div', 'mc-notice-plate__row');
  const retry = button(o.t('app.lite.retry'), true, () => {
    retry.disabled = true;
    note.textContent = o.t('app.lite.retry.busy');
    void o.retry().then((ok) => {
      note.textContent = o.t(ok ? 'app.lite.retry.ok' : 'app.lite.retry.fail');
      retry.disabled = ok;
    });
  });
  const okBtn = button(o.t('app.lite.ok'), false, () => dlg.close());
  row.append(retry, okBtn);
  plate.append(h, el('p', undefined, o.t('app.lite.sheet.body')), el('p', undefined, o.t('app.lite.sheet.where')), list);
  if (o.code) plate.appendChild(el('small', undefined, o.t('app.lite.code', { code: o.code })));
  plate.append(note, row);
  dlg.appendChild(plate);
  dlg.addEventListener('close', () => dlg.remove());
  dlg.addEventListener('click', (e) => { if (e.target === dlg) dlg.close(); });
  document.body.appendChild(dlg);
  dlg.showModal();
  return dlg;
}

/** The read-only banner (a slice was written by a newer build; architecture §4.2). */
export function showReadOnlyBanner(host: HTMLElement, t: TFunction): HTMLElement {
  const bar = el('div', 'mc-banner mc-banner--warn');
  bar.dataset.banner = 'readonly';
  bar.setAttribute('role', 'status');
  const text = el('div', 'mc-banner__text');
  text.append(el('b', undefined, `${t('app.save.readonly.title')}.`), document.createTextNode(` ${t('app.save.readonly.body')}`));
  const close = el('button', 'mc-banner__close', '✕');
  close.type = 'button';
  close.setAttribute('aria-label', t('app.lite.close'));
  close.addEventListener('click', () => bar.remove());
  bar.append(text, close);
  host.appendChild(bar);
  return bar;
}

const DROP_SVG = `<svg class="mc-stage-notice__drop" viewBox="0 0 26 34" aria-hidden="true"><path d="M13 1 C13 1 2 15 2 22 C2 28.6 7 33 13 33 C19 33 24 28.6 24 22 C24 15 13 1 13 1 Z" fill="#17110D"/><path d="M8 21 C8 18 10 15 11.5 13" stroke="#5A4632" stroke-width="2" fill="none" stroke-linecap="round"/></svg>`;

export interface StageNotice { fail(onRestart: () => void, onLite: () => void): void; close(): void }

/** E6: «Чернила пролились — перерисовываем…» over the stage while the renderer is re-created. */
export function showStageNotice(host: HTMLElement, t: TFunction): StageNotice {
  host.querySelector('.mc-stage-notice')?.remove();
  const box = el('div', 'mc-stage-notice');
  box.setAttribute('role', 'status');
  box.innerHTML = DROP_SVG;
  const line = el('div', undefined, t('app.gpu.lost'));
  box.appendChild(line);
  host.appendChild(box);
  return {
    fail(onRestart, onLite) {
      line.textContent = t('app.gpu.lost.failed');
      box.querySelector('svg')?.remove();
      const row = el('div', 'mc-notice-plate__row');
      row.append(button(t('app.gpu.restart'), true, onRestart), button(t('app.gpu.lite'), false, () => { box.remove(); onLite(); }));
      box.appendChild(row);
    },
    close() { box.remove(); },
  };
}

/** «Открыто в другой вкладке» with [Играть здесь] (ui-ux §3.4). Returns a remover. */
export function showSecondTab(host: HTMLElement, t: TFunction, onTake: () => void): () => void {
  host.querySelector('.mc-fullnotice[data-notice="tab"]')?.remove();
  const wrap = el('div', 'mc-fullnotice');
  wrap.dataset.notice = 'tab';
  const plate = el('div', 'mc-notice-plate');
  plate.style.maxWidth = '440px';
  plate.setAttribute('role', 'alertdialog');
  plate.append(el('h2', undefined, t('app.lock.title')), el('p', undefined, t('app.lock.body')));
  const row = el('div', 'mc-notice-plate__row');
  row.appendChild(button(t('app.lock.take'), false, () => { wrap.remove(); onTake(); }));
  plate.appendChild(row);
  wrap.appendChild(plate);
  host.appendChild(wrap);
  return () => wrap.remove();
}

/** E0: the only fatal screen («Летопись не открылась» · [Обновить] · [Скопировать диагностику]). */
export function showFatal(t: TFunction, diagnostics: () => string): void {
  document.querySelector('.mc-fullnotice[data-notice="fatal"]')?.remove();
  const wrap = el('div', 'mc-fullnotice');
  wrap.dataset.notice = 'fatal';
  const plate = el('div', 'mc-notice-plate');
  plate.style.maxWidth = '440px';
  plate.setAttribute('role', 'alertdialog');
  const note = el('div', 'mc-notice-plate__note');
  const row = el('div', 'mc-notice-plate__row');
  row.append(
    button(t('app.fatal.copy'), true, () => {
      const text = diagnostics();
      void navigator.clipboard?.writeText(text).then(() => { note.textContent = t('app.fatal.copied'); }, () => { note.textContent = text.slice(0, 200); });
    }),
    button(t('app.fatal.reload'), false, () => location.reload()),
  );
  plate.append(el('h2', undefined, t('app.fatal.title')), el('p', undefined, t('app.fatal.body')), note, row);
  wrap.appendChild(plate);
  (document.getElementById('overlay') ?? document.body).appendChild(wrap);
}
